import {
  IEquipmentAttributeTypes,
  IShipperLoadTypes,
  IShipperRateTypes,
  Maybe,
} from 'types/graphqlTypes'
import { IEquipAttributeFragment } from '../graphql/EquipAttributeFragment'
import { IShipmentInformationQuery } from '../graphql/ShipmentInformation'
import { IStopInformationFragment } from '../graphql/StopInformationFragment'
import { CreateShipmentSchema, TCreateShipmentSchema } from '../schema'
import { StopSchema, TStopSchema } from '../schema/ShipmentSchema/StopSchema'

const getStopSchema = (stops: IStopInformationFragment[]): TStopSchema[] => {
  const shipmentStops: TStopSchema[] = stops.map(stop => {
    const {
      appointment,
      commodity,
      dropTrailer,
      genericCommodity,
      mergedFacility,
      palletCount,
      specialInstruction,
      stopSequence,
      stopType,
      weight,
    } = stop || {}
    const { facility, shipperFacility } = mergedFacility || {}
    const { uuid: shipperFacilityUuid } = shipperFacility || {}
    const { location, name: facilityName } = facility || {}
    const { address, city, stateCode } = location || {}
    const { appointmentType } = appointment || {}
    const { id: commodityId } = genericCommodity || {}

    return StopSchema.cast({
      stopType,
      stopSequence,
      shipperFacility: {
        id: shipperFacilityUuid,
        text: facilityName,
        city,
        address,
        stateCode,
      },
      appointmentType,
      startAtDate: null,
      startAtTime: null,
      endAtDate: null,
      endAtTime: null,
      dropTrailer,
      appointmentReference: '',
      confirmationReference: '',
      specialInstruction: specialInstruction || '',
      weight,
      palletCount,
      genericCommodityId: commodityId,
      commodity,
      billOfLadingReference: '',
      poReference: '',
      vendorId: null,
    })
  })

  return shipmentStops
}

const getEquipmentAttributes = (
  equipmentAttributes: Maybe<IEquipAttributeFragment[]> = []
): Record<IEquipmentAttributeTypes, string> => {
  let equipmentAttributesObject: Record<string, string> = {}
  equipmentAttributes?.forEach(({ attributeValue, parent }) => {
    // DB is storing this values as strings
    // Old shipment details stores true as "t"
    const isBooleanValue =
      attributeValue === 'false' || attributeValue === 'true' || attributeValue === 't'

    if (parent) {
      equipmentAttributesObject = {
        ...equipmentAttributesObject,
        [parent.key as IEquipmentAttributeTypes]: isBooleanValue
          ? String(attributeValue === 'true' || attributeValue === 't')
          : attributeValue,
      }
    }
  })
  return equipmentAttributesObject
}

export const getCreateShipmentSchema = (
  shipmentInfo: Maybe<IShipmentInformationQuery>
): TCreateShipmentSchema => {
  const { shipment } = shipmentInfo || {}
  const {
    id,
    carrierManager,
    carrierSpecialInstructions,
    characteristics,
    equipment,
    loadingType,
    shipperApplication,
    shipperMiles,
    shipperPerMileFuelRate,
    shipperRate,
    shipperRateType,
    stops = [],
    team,
    totalPalletCount,
    totalWeight,
    equipmentAttributes,
  } = shipment || {}
  const { id: shipperApplicationId = '', name: shipperApplicationName = '' } =
    shipperApplication || {}
  const { key: equipmentTypeKey, parentKey: equipmentParentKey } = equipment || {}
  const { fullName: carrierManagerName, id: carrierManagerId = '' } = carrierManager || {}
  const { hot } = characteristics || {}

  return CreateShipmentSchema.cast({
    shipperApplication: {
      id: shipperApplicationId,
      text: shipperApplicationName,
    },
    equipment: equipmentParentKey || undefined,
    equipmentType: equipmentTypeKey,
    equipmentAttributes: getEquipmentAttributes(equipmentAttributes),
    shipment: {
      stops: getStopSchema(stops),
      opsRep: {
        id: carrierManagerId,
        text: carrierManagerName || '',
      },
      totalWeight,
      totalPallets: totalPalletCount,
      shipperPerMileFuelRate,
      shipperMiles,
      shipperRate,
      shipperRateType: shipperRateType || IShipperRateTypes.contract_primary,
      carrierSpecialInstructions: carrierSpecialInstructions || '',
      team: team || false,
      floorLoaded: loadingType === IShipperLoadTypes.floor_load,
      hot: hot || false,
      originalShipmentId: id ?? null,
      isRelatedShipment: false,
    },
  })
}
